// Lib
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import canvasElementDropAnimationStateManager from './canvasElementDropAnimationStateManager';
import Element from '../element/Element';

// Utils
import { getRenderedCanvasPositionPx } from '../../common/elements/utils/elementPositionUtils';
import { getElementId, getLocationPosition } from '../../common/elements/utils/elementPropertyUtils';

// Selectors
import { getDropState } from '../reducers/draggingSelector';
import { getElements } from '../element/selectors/elementSelector';
import { getCurrentVisibleBoardCanvasOrigin } from '../element/selectors/currentBoardSelector';

// Styles
import './CanvasElement.scss';

const DISABLE_DRAG_CLASS_NAME = 'disable-drag';

/**
 * PERF improvement - We only need the elementID here and that should never change from the initial
 *      render. If that's the case we can use initialProps and avoid suffering a performance hit
 *      by using "ownProps" in the mapStateToProps function.
 */
const makeMapStateToProps = (initialState, initialProps) => (state) => ({
    element: getElements(state).get(initialProps.elementId),
    dropState: getDropState(state, initialProps),
    canvasOrigin: getCurrentVisibleBoardCanvasOrigin(state),
});

@React.memo
@connect(makeMapStateToProps)
@canvasElementDropAnimationStateManager
class CanvasElement extends React.Component {
    constructor(props) {
        super(props);

        // NOTE: Using a class property here, instead of state, because it can potentially change
        // frequently as the mouse moves over the canvas. Using state would cause re-renders and potential jank.
        this.isDragDisabled = false;
    }

    disableDrag = () => {
        this.isDragDisabled = true;

        // Use a requestAnimationFrame to ensure the React render has completed when we try to set the class
        requestAnimationFrame(() => {
            const { canvasElementRef } = this.props;
            if (!canvasElementRef || !canvasElementRef.current) return;
            canvasElementRef.current.classList.add(DISABLE_DRAG_CLASS_NAME);
        });
    };

    enableDrag = () => {
        this.isDragDisabled = false;

        // Use a requestAnimationFrame to ensure the React render has completed when we try to remove the class
        requestAnimationFrame(() => {
            const { canvasElementRef } = this.props;
            if (!canvasElementRef || !canvasElementRef.current) return;
            canvasElementRef.current.classList.remove(DISABLE_DRAG_CLASS_NAME);
        });
    };

    render() {
        const { element, gridSize, sortIndex, canvasOrigin, canvasElementRef, ...rest } = this.props;

        const canvasPosition = getRenderedCanvasPositionPx(getLocationPosition(element), canvasOrigin, gridSize);

        const style = {
            top: `${canvasPosition.y}px`,
            left: `${canvasPosition.x}px`,
            zIndex: sortIndex,
        };

        return (
            <div
                data-element-id={getElementId(element)}
                className={classNames('CanvasElement', { [DISABLE_DRAG_CLASS_NAME]: this.isDragDisabled })}
                style={style}
                ref={canvasElementRef}
            >
                <Element
                    {...rest}
                    element={element}
                    gridSize={gridSize}
                    disableDrag={this.disableDrag}
                    enableDrag={this.enableDrag}
                />
            </div>
        );
    }
}

CanvasElement.propTypes = {
    elementId: PropTypes.string.isRequired,
    element: PropTypes.object,
    canvasOrigin: PropTypes.object,
    gridSize: PropTypes.number.isRequired,
    connectDragSource: PropTypes.func,
    onDrop: PropTypes.func,
    sortIndex: PropTypes.number,

    animating: PropTypes.bool,
    canvasElementRef: PropTypes.object,
};

// Wrap the canvas element so it can be used in the document mode calculations
export default CanvasElement;
